<script setup>
	import { computed, onBeforeUnmount, onMounted, reactive, watch } from "vue";
	import { executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "@/services";
	import { trackContactDialogOpened, setContactSource, clearContactSource } from "@/services/analytics";
	import TestimonialCarousel from "@/components/TestimonialCarousel.vue";
	import lucentaryLogoUrl from "@/assets/img/lucentary-banner-blue.svg";

	const promoPage = "/vibe-code-promotion-classic";

	const testimonials = [
		{
			author: "Geoff A",
			role: "Engineering Leader",
			quote: "Chad is very talented technically, and he brings a wealth of experience and ability to the projects he's involved in. He also has a lot to offer in terms of project management. He is an effective communicator and has experience with a variety of software development methodologies. Finally, and most importantly, Chad is a phenomenal mentor and teacher.",
		},
		{
			author: "Joshua L",
			role: "Founder",
			quote: "Chad is one of those rare individuals whose vision extends beyond just the project at hand. He sees the big picture, both technically, managerially, and in relationships. He is very talented when it comes to technical matters, and also knows how to train and mentor individuals to help them reach their maximum potential.",
		},
		{
			author: "Raul G",
			role: "Product Lead",
			quote: "We hired Chad for a quick turnaround (3 weeks!) SaaS project and he delivered, exceptionally. Our team and, more importantly, the customers felt the demo prototype was professionally done and exceeded expectations. Chad was extremely responsive, knowledgeable and great to work with.",
		},
		{
			author: "Raj S",
			role: "VP Engineering",
			quote: "In a matter of weeks Chad assumed responsibility for the majority of the user interface. Throughout the project Chad offered fresh options and new ideas allowing us to leverage the latest technology capabilities. In addition to development, Chad took a leadership role in defining our build process and policies.",
		},
		{
			author: "Josh S",
			role: "CTO",
			quote: "Chad is easily the smartest and most innovative architect and engineer I have worked with. Rarely do you come across someone with his breadth and command of technical knowledge. He fundamentally understands how to build robust systems that provide value to his clients. He is also a great communicator.",
		},
		{
			author: "Larry M",
			role: "Director of Technology",
			quote: "Chad is a remarkable architect and developer. He was able to deliver on the project, despite the technical and management challenges. With constant change in technical direction, he was able to adapt well. I hope to work with Chad again in the future.",
		},
		{
			author: "AD A",
			role: "Operations Lead",
			quote: "We hired Chad to help us migrate a number of PHP-based applications to .NET as part of a migration off an old intranet. Without existing documentation, Chad was able to work with business users and the existing code base to re-write three applications from the ground up in a matter of weeks.",
		},
		{
			author: "DJ W",
			role: "Solutions Director",
			quote: "Chad Greer is one of the top Microsoft Software Solution Consultants in the market. He has great depth in architecting and developing custom solutions using an Agile approach to solve complex business problems. He has a natural ability to apply his past experiences to new challenges.",
		},
		{
			author: "Tor N",
			role: "Software Architect",
			quote: "Chad is a technically gifted individual, with an eye for detail, and operates well as part of a team in respect of innovative and creative approaches to design, development and implementation. He has an impeccable work ethic and is one of the most innovative software architects I've known.",
		},
		{
			author: "Matt B",
			role: "Principal Engineer",
			quote: "Chad was my counterpart in defining and developing our company's software, including the database, data operations, the presentation software and the overarching architecture. He understands how to write well-formed and maintainable software, but he was a shining star because he understood how to craft an enterprise solution.",
		},
		{
			author: "Chad S",
			role: "Technical Lead",
			quote: "Chad is without a doubt one of the most innovative software architects I've known and a very powerful technical leader. He has a talent for inspiring passion in others. He is very intelligent, has an impeccable work ethic and does his homework. I highly recommend Chad Greer for any technical leadership position.",
		},
	];

	const painPoints = [
		{
			tag: "Unmaintainable",
			title: "Nobody knows how it works",
			description: "The AI wrote it, the contractor vanished, and now changing one thing breaks three others. The knowledge left the building.",
		},
		{
			tag: "Unstable",
			title: "It breaks every time you breathe on it",
			description: "Every deploy is a gamble. You've stopped shipping features because you're afraid of what they'll take down with them.",
		},
		{
			tag: "Unhireable",
			title: "You can't hire your way out",
			description: "Good engineers take one look at the code and pass. The mess is now actively costing you talent, time, and customers.",
		},
	];

	const offerings = [
		{
			label: "Audit",
			title: "Diagnose the chaos",
			description: "We read what the AI actually wrote, map the landmines, and tell you in plain English what's holding the whole thing together.",
		},
		{
			label: "Refactor",
			title: "Stabilize the build",
			description: "Clean, tested, maintainable code that stops breaking every time you breathe on it. Written by humans who have shipped real systems.",
		},
		{
			label: "Ship",
			title: "Make it last",
			description: "Architecture that scales with you instead of collapsing under you. No duct tape, no mystery dependencies, no 3am surprises.",
		},
	];

	const valueIncludes = [
		"A professional team led by Chad Greer, not offshore juniors",
		"A stabilized, tested, documented codebase",
		"A clear roadmap you actually own",
		"50% off your first 6 weeks as a new client",
	];

	const faqs = [
		{
			question: "Is the first call really free?",
			answer: "Yes. It's a 30-minute conversation directly with Chad, a technical expert, not a sales rep. No obligation. Most people leave with a clear plan whether or not they decide to work with us.",
		},
		{
			question: "How much does fixing my software cost?",
			answer: "Every codebase is different, so we scope it on the call and give you a clear range before anything starts. New clients get 50% off their first 6 weeks.",
		},
		{
			question: "What if my code is too far gone?",
			answer: "Then we'll tell you on the first call. We would rather lose a sale than take your money for something that isn't worth saving.",
		},
		{
			question: "Who actually does the work?",
			answer: "Professional engineers led by Chad Greer, who has architected and shipped enterprise software for 20+ years. No handing your project to juniors.",
		},
		{
			question: "How quickly can you start?",
			answer: "We take on a limited number of new clients each month. When there's a fit, we usually begin within a week or two of the call.",
		},
	];

	const data = reactive({
		requestMoreInfo: {
			contactInfo: {
				emailAddress: "",
				name: "",
				text: "",
			},
			isError: false,
			isProcessing: false,
			isShowing: false,
			isSuccess: false,
			topic: "",
			validationErrors: [],
		},
		openFaqIndex: 0,
	});

	const requestMoreInfoContent = {
		General: {
			title: "Talk through your situation",
			description: "Tell us a little about what's going on and we'll get back to you shortly.",
		},
		GetStarted: {
			title: "Let's get started",
			description: "Tell us a bit about your project and we'll reach out to get things moving.",
		},
	};

	const activeRequestMoreInfoContent = computed(() => requestMoreInfoContent[data.requestMoreInfo.topic] || requestMoreInfoContent.General);

	function requestMoreInfo(topic) {
		data.requestMoreInfo.topic = topic;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.isSuccess = false;
		data.requestMoreInfo.isProcessing = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.isShowing = true;
	}

	function closeRequestMoreInfo() {
		data.requestMoreInfo.isShowing = false;
	}

	async function submitRequestForMoreInfo() {
		data.requestMoreInfo.isProcessing = true;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.contactInfo.phoneNumber = "n/a";
		data.requestMoreInfo.contactInfo.recaptchaResponse = "skip";

		await executeServiceCall(() => selfServiceService.public.submitContactRequest(data.requestMoreInfo.contactInfo))
			.then(() => {
				data.requestMoreInfo.isSuccess = true;
			})
			.catch(error => {
				if (error?.response?.status === 400) {
					data.requestMoreInfo.validationErrors = getValidationErrorsFromResponse(error.response).map(validationMessage =>
						validationMessage === "Your E-mail Address is required."
							? "Your E-mail Address or Phone Number is required."
							: validationMessage
					);
				} else {
					console.error(error);
					data.requestMoreInfo.isError = true;
				}
			})
			.finally(() => {
				data.requestMoreInfo.isProcessing = false;
			});
	}

	function handleModalKeydown(event) {
		if (event.key === "Escape") {
			closeRequestMoreInfo();
		}
	}

	function toggleFaq(faqIndex) {
		data.openFaqIndex = data.openFaqIndex === faqIndex ? -1 : faqIndex;
	}

	function scrollToSection(sectionId) {
		const section = document.getElementById(sectionId);

		if (section) {
			section.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}

	watch(() => data.requestMoreInfo.isShowing, (isShowing) => {
		if (isShowing) {
			trackContactDialogOpened({ topic: data.requestMoreInfo.topic });

			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleModalKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleModalKeydown);
		}
	});

	onMounted(() => {
		setContactSource(promoPage);
	});

	onBeforeUnmount(() => {
		clearContactSource();

		document.documentElement.style.overflow = "";
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
		window.removeEventListener("keydown", handleModalKeydown);
	});
</script>

<template>
	<div class="promotion-c">
		<header class="brand-bar">
			<div class="brand-bar-inner">
				<img class="brand-logo" :src="lucentaryLogoUrl" alt="Lucentary" />
			</div>
		</header>

		<section class="hero">
			<div class="hero-decoration" aria-hidden="true">
				<span class="blob blob-one"></span>
				<span class="blob blob-two"></span>
			</div>

			<div class="hero-inner">
				<div class="hero-copy">
					<span class="offer-badge">50% off your first 6 weeks</span>
					<h1 class="hero-title">
						Got a vibe-coded mess?
						<span class="hero-title-accent">We'll clean it up for you.</span>
					</h1>
					<p class="hero-lead">
						AI shipped you something that sort of runs and nobody can maintain. That's exactly
						when we step in, experienced people who quietly fix it, make it solid, and hand it
						back working.
					</p>
					<div class="hero-actions">
						<button type="button" class="btn btn-primary" @click="requestMoreInfo('GetStarted')">Book a Free Call</button>
						<button type="button" class="btn btn-secondary" @click="scrollToSection('how-we-work')">How we work</button>
					</div>
					<p class="hero-reassurance">Talk directly with a technical expert, not a sales rep. Free, and no obligation.</p>
					<p class="hero-trust"><span class="hero-trust-stars" aria-hidden="true">★★★★★</span> Loved by founders &amp; CTOs · professional engineers, not juniors</p>
				</div>

				<aside class="hero-aside">
					<figure class="spotlight-card">
						<div class="spotlight-mark" aria-hidden="true">&ldquo;</div>
						<blockquote class="spotlight-quote">
							Chad is easily the smartest and most innovative engineer I have worked with. He
							builds robust systems that provide real value, and explains the hard parts clearly.
						</blockquote>
						<figcaption class="spotlight-author">
							<span class="spotlight-avatar" aria-hidden="true">J</span>
							<span>
								<span class="spotlight-name">Josh S</span>
								<span class="spotlight-role">Chief Technology Officer</span>
							</span>
						</figcaption>
					</figure>
					<div class="hero-chip hero-chip-one">Humans, not hallucinations</div>
					<div class="hero-chip hero-chip-two">20+ years shipping software</div>
				</aside>
			</div>
		</section>

		<section class="problem">
			<div class="problem-inner">
				<header class="block-header">
					<span class="block-eyebrow">Sound familiar?</span>
					<h2 class="block-heading">If your software feels like a liability, you're not wrong.</h2>
				</header>
				<div class="problem-grid">
					<article v-for="painPoint in painPoints" :key="painPoint.title" class="problem-card">
						<span class="problem-label">{{ painPoint.tag }}</span>
						<h3 class="problem-title">{{ painPoint.title }}</h3>
						<p class="problem-description">{{ painPoint.description }}</p>
					</article>
				</div>
			</div>
		</section>

		<section id="how-we-work" class="offerings">
			<div class="offerings-inner">
				<header class="block-header">
					<span class="block-eyebrow">How we work</span>
					<h2 class="block-heading">Three moves out of the mess</h2>
				</header>
				<div class="offerings-grid">
					<article
						v-for="(offering, offeringIndex) in offerings"
						:key="offering.label"
						class="offering-card"
					>
						<span class="offering-step">{{ String(offeringIndex + 1).padStart(2, "0") }}</span>
						<span class="offering-label">{{ offering.label }}</span>
						<h3 class="offering-title">{{ offering.title }}</h3>
						<p class="offering-description">{{ offering.description }}</p>
					</article>
				</div>
			</div>
		</section>

		<section class="testimonials">
			<div class="testimonials-inner">
				<header class="block-header">
					<span class="block-eyebrow">What clients say</span>
					<h2 class="block-heading">In their own words</h2>
				</header>

				<div class="testimonials-carousel">
					<TestimonialCarousel :items="testimonials">
						<template #default="{ item }">
							<figure class="testimonial-card">
								<div class="testimonial-mark" aria-hidden="true">&ldquo;</div>
								<blockquote class="testimonial-quote">{{ item.quote }}</blockquote>
								<figcaption class="testimonial-author">
									<span class="testimonial-avatar" aria-hidden="true">{{ item.author.charAt(0) }}</span>
									<span>
										<span class="testimonial-name">{{ item.author }}</span>
										<span class="testimonial-role">{{ item.role }}</span>
									</span>
								</figcaption>
							</figure>
						</template>
					</TestimonialCarousel>
				</div>
			</div>
		</section>

		<section class="offer">
			<div class="offer-inner">
				<header class="block-header">
					<span class="block-eyebrow">The offer</span>
					<h2 class="block-heading">What you actually get</h2>
				</header>
				<p class="offer-lead">
					The first call is free, and worth it even if we never work together. You'll talk directly
					with a technical expert, not a sales rep, and leave knowing exactly what's wrong and what
					it takes to fix it.
				</p>
				<div class="offer-card">
					<ul class="offer-list">
						<li v-for="valueItem in valueIncludes" :key="valueItem" class="offer-item">
							<span class="offer-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							{{ valueItem }}
						</li>
					</ul>
				</div>
				<div class="offer-action">
					<button type="button" class="btn btn-primary" @click="requestMoreInfo('GetStarted')">Book your free call</button>
				</div>
			</div>
		</section>

		<section class="faq">
			<div class="faq-inner">
				<header class="block-header">
					<span class="block-eyebrow">Questions</span>
					<h2 class="block-heading">Everything you might be wondering</h2>
				</header>
				<div class="faq-list">
					<div v-for="(faq, faqIndex) in faqs" :key="faq.question" class="faq-item" :class="{ 'faq-item-open': data.openFaqIndex === faqIndex }">
						<button type="button" class="faq-question" :aria-expanded="data.openFaqIndex === faqIndex" @click="toggleFaq(faqIndex)">
							<span>{{ faq.question }}</span>
							<span class="faq-icon" aria-hidden="true"></span>
						</button>
						<div v-show="data.openFaqIndex === faqIndex" class="faq-answer">
							<p>{{ faq.answer }}</p>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section class="cta">
			<div class="cta-inner">
				<div class="cta-card">
					<div class="cta-decoration" aria-hidden="true"></div>
					<h2 class="cta-title">Let's have an honest conversation</h2>
					<p class="cta-lead">
						Tell us what's broken. We'll tell you straight whether software is even
						the answer and what it would really take to fix it. No script, no
						pressure, no fluff.
					</p>
					<div class="cta-actions">
						<button type="button" class="btn btn-light" @click="requestMoreInfo('GetStarted')">Book a Free Call</button>
						<button type="button" class="btn btn-quiet-light" @click="requestMoreInfo('General')">Talk through your situation</button>
					</div>
					<p class="cta-offer">New clients get <strong>50% off the first 6 weeks.</strong></p>
					<p class="cta-note">Free · 30 minutes · talk directly with a technical expert · not a sales call</p>
				</div>
			</div>
		</section>

		<footer class="site-footer">
			<div class="site-footer-inner">
				<img class="footer-logo" :src="lucentaryLogoUrl" alt="Lucentary" />
				<p class="footer-text">Professional software, built by humans. &copy; {{ new Date().getFullYear() }}</p>
			</div>
		</footer>

		<transition name="modal-fade">
			<div v-if="data.requestMoreInfo.isShowing" class="modal-overlay" @click.self="closeRequestMoreInfo">
				<div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="requestMoreInfoTitle" @click.stop>
					<button type="button" class="modal-close" aria-label="Close" @click="closeRequestMoreInfo"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
					<div class="modal-scroll">
						<template v-if="!data.requestMoreInfo.isSuccess">
							<span class="modal-badge">Free · No commitment</span>
							<h2 id="requestMoreInfoTitle" class="modal-title">{{ activeRequestMoreInfoContent.title }}</h2>
							<p class="modal-description">{{ activeRequestMoreInfoContent.description }}</p>
							<div class="modal-field">
								<label for="requestMoreInfoName">Name</label>
								<input id="requestMoreInfoName" v-model="data.requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoEmail">Email or phone number</label>
								<input id="requestMoreInfoEmail" v-model="data.requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoText">What's going on with your software?</label>
								<textarea id="requestMoreInfoText" v-model="data.requestMoreInfo.contactInfo.text" rows="4" :disabled="data.requestMoreInfo.isProcessing"></textarea>
							</div>
							<div v-if="data.requestMoreInfo.validationErrors.length > 0" class="modal-feedback modal-feedback-error" role="alert">
								<p class="modal-feedback-title">Please fix the following:</p>
								<ul class="modal-feedback-list">
									<li v-for="(validationError, validationErrorIndex) in data.requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
								</ul>
							</div>
							<p v-else-if="data.requestMoreInfo.isError" class="modal-feedback modal-feedback-error" role="alert">Something went wrong sending your message. Please try again.</p>
							<button type="button" class="btn btn-primary btn-block" :disabled="data.requestMoreInfo.isProcessing" @click="submitRequestForMoreInfo"><span v-if="data.requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ data.requestMoreInfo.isProcessing ? "Sending…" : "Send message" }}</button>
						</template>
						<div v-else class="modal-success">
							<span class="success-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							<h2 class="modal-title">Message received</h2>
							<p class="modal-description">Thanks for reaching out. We'll be in touch with you shortly.</p>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
	.promotion-c {
		--page: #f4f8fd;
		--card: #ffffff;
		--ink: #182d4d;
		--ink-soft: #44546f;
		--ink-muted: #76859c;
		--blue: #3b6fd4;
		--blue-deep: #2a54a8;
		--blue-soft: #dce8fb;
		--sky: #cfe0fb;
		--sky-light: #e6eefc;
		--border: #e2eaf6;
		--cta: #2a54a8;

		font-family: "Poppins", "Avenir Next", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
		color: var(--ink);
		background: var(--page);
		--carousel-accent: var(--blue);
		--carousel-dot: #c3d3ee;
		overflow-x: hidden;
	}

	.brand-bar {
		padding: clamp(1.25rem, 3vw, 1.75rem) 1.5rem 0;
	}

	.brand-bar-inner {
		max-width: 1180px;
		margin: 0 auto;
	}

	.brand-logo {
		height: clamp(42px, 5vw, 54px);
		width: auto;
		display: block;
	}

	.hero {
		position: relative;
		padding: clamp(2rem, 4vw, 3.25rem) 1.5rem clamp(3rem, 6vw, 5rem);
		overflow: hidden;
		isolation: isolate;
	}

	.hero-decoration {
		position: absolute;
		inset: 0;
		z-index: -1;
		pointer-events: none;
	}

	.blob {
		position: absolute;
		border-radius: 50%;
		filter: blur(10px);
		opacity: 0.6;
	}

	.blob-one {
		top: -140px;
		right: -100px;
		width: 460px;
		height: 460px;
		background: radial-gradient(circle at 30% 30%, var(--sky), transparent 70%);
	}

	.blob-two {
		bottom: -160px;
		left: -120px;
		width: 380px;
		height: 380px;
		background: radial-gradient(circle at 60% 40%, var(--sky-light), transparent 70%);
	}

	.hero-inner {
		max-width: 1180px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 1.05fr 0.95fr;
		gap: clamp(2rem, 5vw, 4.5rem);
		align-items: center;
	}

	.offer-badge {
		display: inline-block;
		padding: 0.55rem 1.2rem;
		border-radius: 999px;
		background: var(--blue);
		color: #ffffff;
		font-size: 0.98rem;
		font-weight: 600;
		margin-bottom: 1.75rem;
		box-shadow: 0 12px 26px -14px rgba(42, 84, 168, 0.7);
	}

	.hero-title {
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(2.5rem, 5vw, 4.3rem);
		line-height: 1.04;
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--ink);
		margin: 0 0 1.6rem;
	}

	.hero-title-accent {
		display: block;
		color: var(--blue-deep);
		font-style: italic;
	}

	.hero-lead {
		font-size: clamp(1.1rem, 1.4vw, 1.32rem);
		line-height: 1.65;
		color: var(--ink-soft);
		max-width: 54ch;
		margin: 0 0 2.25rem;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		flex-wrap: wrap;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 1rem 2.05rem;
		border-radius: 14px;
		font-family: inherit;
		font-size: 1.08rem;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		border: 2px solid transparent;
		transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease, color 0.25s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(42, 84, 168, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(42, 84, 168, 0.75);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(59, 111, 212, 0.4);
		outline-offset: 3px;
	}

	.btn-secondary {
		background: var(--card);
		color: var(--ink);
		border-color: var(--border);
	}

	.btn-secondary:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-secondary:focus-visible {
		outline: 3px solid rgba(24, 45, 77, 0.25);
		outline-offset: 3px;
	}

	.btn-light {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.45);
	}

	.btn-light:hover {
		color: var(--blue-deep);
		transform: translateY(-2px);
		background: #f1f6ff;
	}

	.btn-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.6);
		outline-offset: 3px;
	}

	.btn-quiet-light {
		background: transparent;
		color: #ffffff;
		border-color: transparent;
		text-decoration: underline;
		text-underline-offset: 4px;
		text-decoration-thickness: 2px;
		padding-left: 1rem;
		padding-right: 1rem;
	}

	.btn-quiet-light:hover { color: var(--sky); }

	.btn-quiet-light:focus-visible {
		outline: 3px solid rgba(255, 255, 255, 0.6);
		outline-offset: 3px;
	}

	.hero-reassurance {
		margin: 1.75rem 0 0;
		font-size: 1.02rem;
		color: var(--ink-muted);
	}

	.hero-trust {
		margin: 0.85rem 0 0;
		font-size: 0.95rem;
		color: var(--ink-muted);
	}

	.hero-trust-stars {
		color: #d97706;
		letter-spacing: 0.08em;
		margin-right: 0.4rem;
	}

	.hero-aside {
		position: relative;
	}

	.spotlight-card {
		position: relative;
		margin: 0;
		padding: clamp(1.75rem, 3vw, 2.5rem);
		border-radius: 24px;
		background: var(--card);
		border: 1px solid var(--border);
		box-shadow: 0 34px 64px -36px rgba(24, 45, 77, 0.5);
	}

	.spotlight-mark {
		font-family: "Fraunces", Georgia, serif;
		font-size: 4rem;
		line-height: 0.5;
		color: var(--blue);
		margin-bottom: 0.5rem;
	}

	.spotlight-quote {
		font-size: 1.18rem;
		line-height: 1.55;
		color: var(--ink);
		margin: 0 0 1.5rem;
		font-weight: 500;
	}

	.spotlight-author {
		display: flex;
		align-items: center;
		gap: 0.85rem;
	}

	.spotlight-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 46px;
		height: 46px;
		border-radius: 50%;
		background: var(--blue);
		color: #ffffff;
		font-size: 1.15rem;
		font-weight: 700;
		flex-shrink: 0;
	}

	.spotlight-name {
		display: block;
		font-size: 1.02rem;
		font-weight: 700;
		color: var(--ink);
	}

	.spotlight-role {
		display: block;
		font-size: 0.9rem;
		color: var(--ink-muted);
	}

	.hero-chip {
		position: absolute;
		padding: 0.7rem 1.1rem;
		border-radius: 14px;
		background: var(--card);
		border: 1px solid var(--border);
		font-size: 0.92rem;
		font-weight: 600;
		color: var(--blue-deep);
		box-shadow: 0 18px 34px -22px rgba(24, 45, 77, 0.55);
	}

	.hero-chip-one {
		top: -1.25rem;
		left: -1rem;
	}

	.hero-chip-two {
		bottom: -1.5rem;
		right: -0.75rem;
	}

	.problem {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--card);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.problem-inner {
		max-width: 1160px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.92rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.8rem;
	}

	.block-heading {
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(2rem, 3.2vw, 3rem);
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--ink);
		margin: 0;
	}

	.problem-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.75rem;
	}

	.problem-card {
		padding: 2.25rem 2rem;
		border-radius: 22px;
		background: var(--page);
		border: 1px solid var(--border);
	}

	.problem-label {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: #b4530b;
		margin-bottom: 0.9rem;
	}

	.problem-title {
		font-family: "Fraunces", Georgia, serif;
		font-size: 1.4rem;
		font-weight: 600;
		line-height: 1.2;
		color: var(--ink);
		margin: 0 0 0.75rem;
	}

	.problem-description {
		font-size: 1.04rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0;
	}

	.offerings {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--page);
	}

	.offerings-inner {
		max-width: 1160px;
		margin: 0 auto;
	}

	.offerings-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.75rem;
	}

	.offering-card {
		position: relative;
		padding: 2.5rem 2rem;
		border-radius: 22px;
		background: var(--card);
		border: 1px solid var(--border);
		transition: transform 0.3s ease, box-shadow 0.3s ease;
	}

	.offering-card:hover {
		transform: translateY(-5px);
		box-shadow: 0 28px 54px -32px rgba(24, 45, 77, 0.45);
	}

	.offering-step {
		font-family: "Fraunces", Georgia, serif;
		display: block;
		font-size: 2.4rem;
		font-weight: 600;
		line-height: 1;
		color: var(--sky);
		margin-bottom: 0.75rem;
	}

	.offering-label {
		display: inline-block;
		font-size: 0.88rem;
		font-weight: 700;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.9rem;
	}

	.offering-title {
		font-family: "Fraunces", Georgia, serif;
		font-size: 1.5rem;
		font-weight: 600;
		line-height: 1.2;
		color: var(--ink);
		margin: 0 0 0.9rem;
	}

	.offering-description {
		font-size: 1.06rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0;
	}

	.testimonials {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--card);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.testimonials-inner {
		max-width: 1240px;
		margin: 0 auto;
	}

	.testimonials-carousel {
		margin-top: 1rem;
	}

	.testimonial-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 2.25rem 2rem 2rem;
		border-radius: 22px;
		background: var(--page);
		border: 1px solid var(--border);
		margin: 0;
		box-sizing: border-box;
	}

	.testimonial-mark {
		font-family: "Fraunces", Georgia, serif;
		font-size: 3.5rem;
		line-height: 0.5;
		color: var(--blue);
		margin-bottom: 0.65rem;
	}

	.testimonial-quote {
		flex: 1;
		font-size: 1.05rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.testimonial-author {
		display: flex;
		align-items: center;
		gap: 0.7rem;
	}

	.testimonial-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.98rem;
		font-weight: 700;
		flex-shrink: 0;
	}

	.testimonial-name {
		display: block;
		font-size: 1rem;
		font-weight: 700;
		color: var(--ink);
	}

	.testimonial-role {
		display: block;
		font-size: 0.88rem;
		font-weight: 500;
		color: var(--ink-muted);
	}

	.offer {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--page);
	}

	.offer-inner {
		max-width: 720px;
		margin: 0 auto;
	}

	.offer-lead {
		max-width: 56ch;
		margin: 0 auto 2.25rem;
		text-align: center;
		font-size: clamp(1.06rem, 1.3vw, 1.2rem);
		line-height: 1.65;
		color: var(--ink-soft);
	}

	.offer-card {
		max-width: 560px;
		margin: 0 auto;
		padding: clamp(1.75rem, 3vw, 2.5rem);
		border-radius: 24px;
		background: var(--card);
		border: 1px solid var(--border);
		box-shadow: 0 28px 54px -34px rgba(24, 45, 77, 0.45);
	}

	.offer-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 1.1rem;
	}

	.offer-item {
		display: flex;
		align-items: flex-start;
		gap: 0.85rem;
		font-size: 1.06rem;
		line-height: 1.5;
		color: var(--ink);
	}

	.offer-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		flex-shrink: 0;
		margin-top: 0.1rem;
	}

	.offer-check svg {
		width: 15px;
		height: 15px;
	}

	.offer-action {
		text-align: center;
		margin-top: 2.25rem;
	}

	.faq {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--card);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.faq-inner {
		max-width: 820px;
		margin: 0 auto;
	}

	.faq-list {
		display: grid;
		gap: 1rem;
	}

	.faq-item {
		background: var(--page);
		border: 1px solid var(--border);
		border-radius: 18px;
		overflow: hidden;
		transition: border-color 0.2s ease, box-shadow 0.2s ease;
	}

	.faq-item-open {
		border-color: var(--blue);
		box-shadow: 0 18px 40px -28px rgba(59, 111, 212, 0.5);
	}

	.faq-question {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 1.4rem 1.6rem;
		background: transparent;
		border: none;
		cursor: pointer;
		font-family: inherit;
		font-size: 1.12rem;
		font-weight: 600;
		text-align: left;
		color: var(--ink);
	}

	.faq-question:focus-visible {
		outline: 3px solid rgba(59, 111, 212, 0.4);
		outline-offset: -3px;
	}

	.faq-icon {
		position: relative;
		width: 18px;
		height: 18px;
		flex-shrink: 0;
	}

	.faq-icon::before,
	.faq-icon::after {
		content: "";
		position: absolute;
		background: var(--blue);
		border-radius: 2px;
		transition: transform 0.25s ease, opacity 0.25s ease;
	}

	.faq-icon::before {
		top: 8px;
		left: 0;
		width: 18px;
		height: 2px;
	}

	.faq-icon::after {
		top: 0;
		left: 8px;
		width: 2px;
		height: 18px;
	}

	.faq-item-open .faq-icon::after {
		transform: scaleY(0);
		opacity: 0;
	}

	.faq-answer {
		padding: 0 1.6rem 1.5rem;
	}

	.faq-answer p {
		margin: 0;
		font-size: 1.05rem;
		line-height: 1.65;
		color: var(--ink-soft);
	}

	.cta {
		padding: clamp(3.5rem, 6vw, 5.5rem) 1.5rem;
		background: var(--page);
	}

	.cta-inner {
		max-width: 1000px;
		margin: 0 auto;
	}

	.cta-card {
		position: relative;
		overflow: hidden;
		padding: clamp(3rem, 5vw, 4.5rem) clamp(1.75rem, 4vw, 4rem);
		border-radius: 28px;
		background: var(--cta);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(42, 84, 168, 0.75);
	}

	.cta-decoration {
		position: absolute;
		top: -50%;
		right: -10%;
		width: 420px;
		height: 420px;
		border-radius: 50%;
		background: radial-gradient(circle, rgba(255, 255, 255, 0.16), transparent 70%);
		pointer-events: none;
	}

	.cta-title {
		position: relative;
		font-family: "Fraunces", Georgia, serif;
		font-size: clamp(2rem, 3.5vw, 3rem);
		font-weight: 600;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1.5rem;
		max-width: 20ch;
		color: #ffffff;
	}

	.cta-lead {
		position: relative;
		font-size: clamp(1.1rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: rgba(255, 255, 255, 0.92);
		max-width: 56ch;
		margin: 0 auto 2.5rem;
	}

	.cta-actions {
		position: relative;
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 1.75rem;
	}

	.cta-offer {
		position: relative;
		font-size: 1.05rem;
		color: rgba(255, 255, 255, 0.92);
		margin: 0;
	}

	.cta-offer strong { color: #ffffff; }

	.cta-note {
		position: relative;
		margin: 0.85rem auto 0;
		font-size: 0.92rem;
		color: rgba(255, 255, 255, 0.75);
		letter-spacing: 0.02em;
		max-width: 60ch;
		line-height: 1.55;
	}

	.site-footer {
		padding: clamp(2.5rem, 5vw, 3.5rem) 1.5rem;
		background: var(--card);
		border-top: 1px solid var(--border);
	}

	.site-footer-inner {
		max-width: 1180px;
		margin: 0 auto;
		text-align: center;
	}

	.footer-logo {
		height: clamp(46px, 5vw, 56px);
		width: auto;
		display: block;
		margin: 0 auto 1rem;
	}

	.footer-text {
		margin: 0;
		font-size: 0.92rem;
		color: var(--ink-muted);
	}

	.modal-overlay {
		--page: #f4f8fd;
		--card: #ffffff;
		--ink: #182d4d;
		--ink-soft: #44546f;
		--ink-muted: #76859c;
		--blue: #3b6fd4;
		--blue-deep: #2a54a8;
		--blue-soft: #dce8fb;
		--border: #e2eaf6;
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(24, 45, 77, 0.5);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		cursor: pointer;
		z-index: 100000;
		pointer-events: auto;
	}

	.modal-dialog {
		position: relative;
		pointer-events: auto;
		width: 100%;
		max-width: 500px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow: hidden;
		border-radius: 24px;
		background: var(--card);
		box-shadow: 0 40px 90px -30px rgba(24, 45, 77, 0.5);
		cursor: default;
	}

	.modal-scroll {
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(2.25rem, 4vw, 2.75rem) clamp(1.75rem, 3vw, 2.25rem);
	}

	.modal-close {
		position: absolute;
		top: 1.1rem;
		right: 1.25rem;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.9rem;
		line-height: 1;
		border-radius: 12px;
		border: none;
		background: var(--page);
		color: var(--ink-muted);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;
		z-index: 1;
	}

	.modal-close:hover {
		background: var(--blue-soft);
		color: var(--blue-deep);
	}

	.modal-close svg {
		width: 18px;
		height: 18px;
	}

	.modal-badge {
		display: inline-block;
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.04em;
		margin-bottom: 0.9rem;
	}

	.btn-block {
		width: 100%;
	}

	.modal-title {
		font-family: "Fraunces", Georgia, serif;
		font-size: 1.9rem;
		font-weight: 600;
		margin: 0 0 0.6rem;
		color: var(--ink);
	}

	.modal-description {
		font-size: 1.05rem;
		line-height: 1.55;
		color: var(--ink-soft);
		margin: 0 0 1.75rem;
	}

	.modal-field {
		margin-bottom: 1.2rem;
		text-align: left;
	}

	.modal-field label {
		display: block;
		font-size: 1rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.5rem;
	}

	.modal-field input,
	.modal-field textarea {
		width: 100%;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		border: 2px solid var(--border);
		background: var(--page);
		font-family: inherit;
		font-size: 1.05rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.modal-field textarea { resize: vertical; }

	.modal-field input:focus,
	.modal-field textarea:focus {
		outline: none;
		background: #ffffff;
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(59, 111, 212, 0.18);
	}

	.modal-field input:disabled,
	.modal-field textarea:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		-webkit-animation: btn-spin 0.7s linear infinite;
		animation: btn-spin 0.7s linear infinite;
	}

	@-webkit-keyframes btn-spin {
		to { -webkit-transform: rotate(360deg); }
	}

	@keyframes btn-spin {
		to { transform: rotate(360deg); }
	}

	.modal-success {
		text-align: center;
		padding: 0.75rem 0.25rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.modal-feedback {
		margin: 0 0 1.2rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.98rem;
		font-weight: 500;
		text-align: left;
	}

	.modal-feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.modal-feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.modal-feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.modal-feedback-list li {
		margin-bottom: 0.2rem;
	}

	.modal-fade-enter-active,
	.modal-fade-leave-active { transition: opacity 0.25s ease; }

	.modal-fade-enter-active .modal-dialog,
	.modal-fade-leave-active .modal-dialog { transition: transform 0.25s ease, opacity 0.25s ease; }

	.modal-fade-enter-from,
	.modal-fade-leave-to { opacity: 0; }

	.modal-fade-enter-from .modal-dialog,
	.modal-fade-leave-to .modal-dialog { transform: translateY(14px) scale(0.98); opacity: 0; }

	@media (max-width: 900px) {
		.hero-inner { grid-template-columns: 1fr; }

		.hero-aside { margin-top: 1rem; }

		.hero-chip-one { left: 0; }

		.hero-chip-two { right: 0; }

		.problem-grid,
		.offerings-grid { grid-template-columns: 1fr; }
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions { flex-direction: column; align-items: stretch; }

		.btn { width: 100%; }

		.hero-chip { display: none; }
	}
</style>